import * as React from 'react'
import toast from 'react-hot-toast'
import { sendCommand } from '../../../helpers/commandHelper.ts'
import dataStore from '../../../store/dataStore.ts'
import { IDevice } from '../../../interfaces/types.ts'
import { AsyncButton } from '../AsyncButton/AsyncButton.tsx'
import { PopupPortal } from '../Popup/PopupPortal.tsx'
import {
    hasScheduledSleep,
    getWakeReasonText,
    convertToSleepCommand,
} from '../../../helpers/sleepHelper.ts'
import { TimePicker } from '../TimePicker/TimePicker.tsx'
import { DateTime } from 'luxon'
import authStore from '../../../store/authStore.ts'
import controlStore from '../../../store/controlStore.ts'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMoon } from '@fortawesome/free-solid-svg-icons/faMoon'
import { AlarmClockIcon } from '../../svgs/AlarmClockIcon.tsx'

interface Props {
    tracker: IDevice
    open: boolean
    setOpen: (o: boolean) => void
    disabled?: boolean
}

const formatTime = (timeStr: string): string => {
    const [h, m] = timeStr.split(':').map(Number)
    const dt = DateTime.now().set({ hour: h, minute: m })
    return dt.toFormat('h:mm a')
}

export const ScheduleSleep: React.FC<Props> = (props) => {
    const { tracker, open, setOpen, disabled } = props

    const [startTime, setStartTime] = React.useState('22:00')
    const [endTime, setEndTime] = React.useState('06:00')
    const [sleepRepeat, setSleepRepeat] = React.useState(false)
    const [errors, setErrors] = React.useState<{ start?: string; end?: string; same?: string }>({})

    const [petName, setPetName] = React.useState('')
    const pets = dataStore(s => s.pets)
    const isMobile = controlStore(s => s.isOnMobileDevice)

    const status = tracker.status
    const scheduled = hasScheduledSleep(status)
    const isSleeping = status.sleeping === true
    const wakeReasonText = getWakeReasonText(status.wakeReason)

    // Derived display values for the status card
    const [scheduleStartDisplay, setScheduleStartDisplay] = React.useState('')
    const [scheduleEndDisplay, setScheduleEndDisplay] = React.useState('')

    React.useEffect(() => {
        if (!open) return
        const pet = pets.find(p => p._id === tracker.petId)
        setPetName(pet?.petName || '')
        setErrors({})

        if (scheduled) {
            const zone = authStore.getState().timezone.value
            // Both one-time and repeat now use a full Unix timestamp for sleepStart
            const startDt = DateTime.fromSeconds(status.sleepStart).setZone(zone)
            const endDt = startDt.plus({ minutes: status.sleepDuration })
            setScheduleStartDisplay(startDt.toFormat('h:mm a'))
            setScheduleEndDisplay(endDt.toFormat('h:mm a'))
            setSleepRepeat(status.sleepRepeat)
        } else {
            setStartTime('22:00')
            setEndTime('06:00')
            setSleepRepeat(false)
            setScheduleStartDisplay('')
            setScheduleEndDisplay('')
            setMode('presets')
            setSelectedPreset(null)
        }
    }, [open])

    const validate = (): boolean => {
        const newErrors: { start?: string; end?: string; same?: string } = {}
        if (!startTime) newErrors.start = 'A start time is required'
        if (!endTime) newErrors.end = 'An end time is required'
        if (startTime && endTime && startTime === endTime) newErrors.same = 'Start and end times cannot be the same'
        if (startTime && endTime && startTime !== endTime) {
            const { duration } = convertToSleepCommand(startTime, endTime, sleepRepeat)
            if (duration > 1435) newErrors.same = 'Maximum sleep duration is 23 hours 55 minutes'
        }
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const doSchedule = async () => {
        if (!validate()) return
        const { startEpoch, duration } = convertToSleepCommand(startTime, endTime, sleepRepeat)
        const success = await sendCommand('sleep', tracker.iccid, {
            sleepStart: startEpoch,
            sleepDuration: duration,
            sleepRepeat,
        })
        setOpen(false)
        if (success) toast.success('Sleep schedule command sent')
        else toast.error('Error, sleep schedule command failed. Try again later.')
    }

    const doCancelSchedule = async () => {
        const success = await sendCommand('sleepCancel', tracker.iccid, null)
        setOpen(false)
        if (success) toast.success('Sleep schedule cancelled')
        else toast.error('Error, cancel sleep schedule failed. Try again later.')
    }

    // ── Scheduled mode ──────────────────────────────────────────────────────
    const scheduledPanel = (
        <div className={`flex flex-col gap-4 w-full p-6 min-w-[22rem] ${isMobile ? 'w-[90vw]' : ''}`}>
            <div className={'prose flex flex-col items-center gap-0'}>
                <h2 className={'mt-0 mb-0 text-primary'}>Gibi Tracker Sleep</h2>
                <div className={'text-md font-semibold text-primary'}>{petName}</div>
            </div>

            {/* Status card */}
            <div className={`rounded-xl border-2 p-4 flex flex-col gap-2 ${isSleeping ? 'border-success bg-success/10' : 'border-warning bg-warning/10'}`}>
                <div className={'flex items-center gap-2'}>
                    {isSleeping
                        ? <FontAwesomeIcon icon={faMoon} className={'text-success text-lg'} />
                        : <AlarmClockIcon color={'hsl(var(--wa))'} size={20} />
                    }
                    <span className={'font-bold text-base'}>
                        {isSleeping ? 'Sleeping now' : 'Sleep scheduled'}
                    </span>
                </div>

                <div className={'flex items-baseline gap-2 text-2xl font-bold tracking-tight'}>
                    <span>{scheduleStartDisplay}</span>
                    <span className={'text-base font-normal text-gray-500'}>to</span>
                    <span>{scheduleEndDisplay}</span>
                </div>

                <div className={'text-sm text-gray-500'}>
                    {status.sleepRepeat ? 'Repeats daily' : 'One-time'}
                </div>
            </div>

            {wakeReasonText && (
                <div className={'text-sm text-gray-500 text-center'}>{wakeReasonText}</div>
            )}
        </div>
    )

    const scheduledButtons = (
        <div className={'px-6 pb-6 flex justify-between items-center w-full'}>
            <button className={'btn btn-secondary btn-ghost text-secondary btn-sm'} onClick={() => setOpen(false)}>
                Close
            </button>
            <button
                className={'btn btn-outlined btn-secondary btn-sm'}
                onClick={doCancelSchedule}
                disabled={isSleeping}
            >
                Cancel Schedule
            </button>
        </div>
    )

    // ── Unscheduled / form mode ──────────────────────────────────────────────
    const PRESETS = [
        { label: '8hr', hours: 8 },
        { label: '10hr', hours: 10 },
        { label: '12hr', hours: 12 },
    ]
    const [selectedPreset, setSelectedPreset] = React.useState<number | null>(null)
    const [mode, setMode] = React.useState<'presets' | 'custom' | 'preset-detail'>('presets')

    const applyPreset = (hours: number, index: number) => {
        setSelectedPreset(index)
        setSleepRepeat(true)
        setStartTime('22:00')
        const endH = (22 + hours) % 24
        setEndTime(`${String(endH).padStart(2, '0')}:00`)
        setErrors({})
        setMode('preset-detail')
    }

    const handleStartChange = (v: string) => {
        setStartTime(v)
        setErrors({})
        if (selectedPreset !== null) {
            const hours = PRESETS[selectedPreset].hours
            const [h, m] = v.split(':').map(Number)
            const endH = (h + hours) % 24
            setEndTime(`${String(endH).padStart(2, '0')}:${String(m).padStart(2, '0')}`)
        }
    }

    const wakeDisplay = React.useMemo(() => {
        if (!endTime) return ''
        return formatTime(endTime)
    }, [endTime])

    const goBack = () => {
        setMode('presets')
        setSelectedPreset(null)
    }

    const sleepTip = 'Save battery by sleeping your tracker when your pet is home for the night.'

    // ── Preset selection view (initial) ──
    const presetView = (
        <div className={`flex flex-col gap-4 w-full p-6 min-w-[22rem] ${isMobile ? 'w-[90vw]' : ''}`}>
            <div className={'prose flex flex-col items-center gap-1'}>
                <h2 className={'mt-0 mb-0 text-primary'}>{petName}</h2>
                <div className={'text-primary flex items-center gap-2'}>
                    <FontAwesomeIcon icon={faMoon} className={'text-secondary'} />
                    <span className={'text-md font-semibold'}>Sleep Schedule</span>
                </div>
            </div>

            <div className={'text-sm text-primary text-center bg-base-100 rounded-lg p-3'}>
                {sleepTip}
            </div>

            <div className={'flex flex-col items-center gap-3 pt-2'}>
                <div className={'text-sm font-semibold text-primary'}>How long should your tracker sleep?</div>
                <div className={'flex justify-center gap-3'}>
                    {PRESETS.map((p, i) => (
                        <button
                            key={p.label}
                            className={'btn btn-md btn-outline btn-secondary min-w-[5rem]'}
                            onClick={() => applyPreset(p.hours, i)}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
                <button className={'text-sm text-secondary underline mt-3 bg-transparent border-none cursor-pointer'}
                    onClick={() => setMode('custom')}>
                    Custom schedule
                </button>
            </div>
        </div>
    )

    // ── Preset detail view (after picking a duration) ──
    const presetDetailView = (
        <div className={`flex flex-col gap-4 w-full p-6 min-w-[22rem] ${isMobile ? 'w-[90vw]' : ''}`}>
            <div className={'prose flex flex-col items-center gap-1'}>
                <h2 className={'mt-0 mb-0 text-primary'}>{petName}</h2>
                <div className={'text-primary flex items-center gap-2'}>
                    <FontAwesomeIcon icon={faMoon} className={'text-secondary'} />
                    <span className={'text-md font-semibold'}>
                        {selectedPreset !== null ? `${PRESETS[selectedPreset].hours} Hour Daily Sleep` : 'Sleep Schedule'}
                    </span>
                </div>
            </div>

            <div className={'flex flex-col gap-3 w-full max-w-xs mx-auto'}>
                <TimePicker label="Start Time" value={startTime} onChange={handleStartChange} large={isMobile} />

                <div className={'flex flex-col gap-1'}>
                    <label className={'text-sm font-semibold'}>Wake Time</label>
                    <div className={'text-lg font-bold text-primary'}>{wakeDisplay}</div>
                </div>

                <div className={'text-xs text-gray-500 text-center'}>Repeats daily</div>
            </div>

            <button className={'text-sm text-secondary underline bg-transparent border-none cursor-pointer'}
                onClick={goBack}>
                ← Back
            </button>
        </div>
    )

    // ── Custom view (full control) ──
    const customView = (
        <div className={`flex flex-col gap-4 w-full p-6 min-w-[22rem] ${isMobile ? 'w-[90vw]' : ''}`}>
            <div className={'prose flex flex-col items-center gap-1'}>
                <h2 className={'mt-0 mb-0 text-primary'}>{petName}</h2>
                <div className={'text-primary flex items-center gap-2'}>
                    <FontAwesomeIcon icon={faMoon} className={'text-secondary'} />
                    <span className={'text-md font-semibold'}>Custom Sleep Schedule</span>
                </div>
            </div>

            <div className={'flex flex-col gap-3 w-full max-w-xs mx-auto'}>
                <div className={'flex flex-col gap-1'}>
                    <TimePicker label="Start Time" value={startTime} onChange={(v) => { setStartTime(v); setErrors({}) }} large={isMobile} />
                    {errors.start && <span className={'text-error text-xs'}>{errors.start}</span>}
                </div>

                <div className={'flex flex-col gap-1'}>
                    <TimePicker label="End Time" value={endTime} onChange={(v) => { setEndTime(v); setErrors({}) }} large={isMobile} />
                    {errors.end && <span className={'text-error text-xs'}>{errors.end}</span>}
                </div>

                {errors.same && <span className={'text-error text-xs'}>{errors.same}</span>}

                <div className={'flex flex-col gap-1 items-center'}>
                    <label className={'text-sm font-semibold'}>Repeat</label>
                    <div className={'flex gap-4 justify-center'}>
                        <label className={'flex items-center gap-1 cursor-pointer'}>
                            <input type="radio" name="sleepRepeat" className={'radio radio-sm radio-secondary'}
                                checked={!sleepRepeat} onChange={() => setSleepRepeat(false)} />
                            <span className={'text-sm'}>One-time</span>
                        </label>
                        <label className={'flex items-center gap-1 cursor-pointer'}>
                            <input type="radio" name="sleepRepeat" className={'radio radio-sm radio-secondary'}
                                checked={sleepRepeat} onChange={() => setSleepRepeat(true)} />
                            <span className={'text-sm'}>Daily</span>
                        </label>
                    </div>
                </div>
            </div>

            <button className={'text-sm text-secondary underline bg-transparent border-none cursor-pointer'}
                onClick={goBack}>
                ← Back
            </button>
        </div>
    )

    const formPanel = mode === 'presets' ? presetView : mode === 'preset-detail' ? presetDetailView : customView

    const formButtons = (
        <div className={'px-6 pb-6 flex justify-between items-center w-full'}>
            <button className={'btn btn-secondary btn-ghost text-secondary btn-sm'} onClick={() => setOpen(false)}>
                Close
            </button>
            {mode !== 'presets' && (
                <AsyncButton classNames={'btn btn-outlined btn-secondary btn-sm'} onClick={doSchedule} disabled={disabled}>
                    Schedule
                </AsyncButton>
            )}
        </div>
    )

    return (
        <PopupPortal open={open}>
            {scheduled ? scheduledPanel : formPanel}
            {scheduled ? scheduledButtons : formButtons}
        </PopupPortal>
    )
}
